import { Box, Icon, Stack, Typography } from '@mui/material';
import { Facility } from '@schooly/api';
import { categorizeFacilities, getChildrenByParentId } from '@schooly/hooks/use-facilities';
import { Counter, DoneIcon } from '@schooly/style';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { DropdownSelectRow } from '../DropdownSelectRow';

export type SelectedRoom = { id: string; name: string };

const OTHERS_ID = 'others';
const EXPANDED_ROW_CLASS = 'expanded';

type RoomSelectContentProps = {
  selectedRoom: SelectedRoom | null;
  facilities: Facility[];
  query: string;
  onSelectRoom: (room: SelectedRoom | null) => void;
};

export const RoomSelectContent: FC<RoomSelectContentProps> = ({
  selectedRoom,
  facilities,
  query,
  onSelectRoom,
}) => {
  const { formatMessage } = useIntl();
  const leftPanelRef = useRef<HTMLDivElement>(null);

  const { standaloneBuildings, standaloneRooms, childrenByParentId, campusesWithBuildings } =
    useMemo(() => {
      const categorized = categorizeFacilities(facilities);

      const campusesWithBuildings = categorized.campuses.reduce<
        Array<{ campus: Facility; buildings: Facility[] }>
      >((acc, campus) => {
        const buildings = getChildrenByParentId(categorized.childrenByParentId, campus.id);
        if (buildings.length > 0) acc.push({ campus, buildings });
        return acc;
      }, []);

      return { ...categorized, campusesWithBuildings };
    }, [facilities]);

  const filterByQuery = useCallback(
    (items: Facility[]) => {
      if (!query) return items;
      return items.filter((f) => f.name.toLowerCase().includes(query.toLowerCase()));
    },
    [query],
  );

  const selectedRoomParentId = useMemo(() => {
    if (!selectedRoom) return undefined;
    const facility = facilities.find((f) => f.id === selectedRoom.id);
    return facility?.parent_id ?? OTHERS_ID;
  }, [selectedRoom, facilities]);

  const getInitExpandedFacilityId = useCallback(() => {
    if (selectedRoomParentId) return selectedRoomParentId;
    if (campusesWithBuildings[0]?.buildings[0]) return campusesWithBuildings[0].buildings[0].id;
    if (standaloneBuildings[0]) return standaloneBuildings[0].id;
    if (standaloneRooms.length > 0) return OTHERS_ID;
    return '';
  }, [selectedRoomParentId, campusesWithBuildings, standaloneBuildings, standaloneRooms]);

  const [expandedFacilityId, setExpandedFacilityId] = useState<string>(getInitExpandedFacilityId());

  const hasOnlyRooms = campusesWithBuildings.length === 0 && standaloneBuildings.length === 0;

  useEffect(() => {
    requestAnimationFrame(() => {
      leftPanelRef.current
        ?.querySelector<HTMLElement>(`.${EXPANDED_ROW_CLASS}`)
        ?.scrollIntoView({ block: 'center' });
    });
  }, []);

  const { roomsByParentId, currentRooms } = useMemo(() => {
    const roomsByParentId: Record<string, Facility[]> = {};

    for (const [parentId, children] of Object.entries(childrenByParentId)) {
      roomsByParentId[parentId] = filterByQuery(children);
    }
    roomsByParentId[OTHERS_ID] = filterByQuery(standaloneRooms);

    return { currentRooms: roomsByParentId[expandedFacilityId] ?? [], roomsByParentId };
  }, [filterByQuery, standaloneRooms, expandedFacilityId, childrenByParentId]);

  const handleSelectRoom = useCallback(
    (room: Facility) => {
      onSelectRoom(selectedRoom?.id === room.id ? null : { id: room.id, name: room.name });
    },
    [onSelectRoom, selectedRoom?.id],
  );

  const renderRoomsList = () => {
    if (currentRooms.length === 0) {
      const messageId =
        expandedFacilityId === OTHERS_ID || hasOnlyRooms
          ? 'timetable-NoRoomsFound'
          : 'timetable-NoRoomsInBuilding';

      return (
        <Typography px={1} color="text.primary">
          {formatMessage({ id: messageId })}
        </Typography>
      );
    }

    return currentRooms.map((room) => (
      <DropdownSelectRow
        key={room.id}
        isSelected={selectedRoom?.id === room.id}
        onClick={() => handleSelectRoom(room)}
        sx={(theme) => ({
          px: 1,
          py: 0.5,
          '.MuiTypography-root': {
            whiteSpace: 'normal',
            ...(hasOnlyRooms ? {} : theme.typography.body1),
          },
        })}
      >
        {room.name}
      </DropdownSelectRow>
    ));
  };

  if (hasOnlyRooms) {
    return (
      <Stack sx={{ overflowY: 'auto', px: 1, gap: 0.5, py: 1, maxHeight: 200, minWidth: 200 }}>
        {renderRoomsList()}
      </Stack>
    );
  }

  return (
    <Stack sx={{ flexDirection: 'row', maxHeight: 200, minWidth: 500 }}>
      <Stack ref={leftPanelRef} sx={{ overflowY: 'auto', p: 2, pl: 1, gap: 0.5, width: '50%' }}>
        {campusesWithBuildings.map(({ campus, buildings }) => (
          <Stack key={campus.id} gap={0.5} pl={1}>
            <Typography py={0.5} color="text.primary">
              {campus.name}
            </Typography>
            {buildings.map((building) => (
              <ExpandableRow
                key={building.id}
                name={building.name}
                count={(roomsByParentId[building.id] ?? []).length}
                isExpanded={expandedFacilityId === building.id}
                onClick={() => setExpandedFacilityId(building.id)}
                isSelected={selectedRoomParentId === building.id}
              />
            ))}
          </Stack>
        ))}

        {standaloneBuildings.map((building) => (
          <ExpandableRow
            key={building.id}
            name={building.name}
            count={(roomsByParentId[building.id] ?? []).length}
            isExpanded={expandedFacilityId === building.id}
            onClick={() => setExpandedFacilityId(building.id)}
            isSelected={selectedRoomParentId === building.id}
          />
        ))}

        {standaloneRooms.length > 0 && (
          <ExpandableRow
            name={formatMessage({ id: 'section-Others' })}
            count={(roomsByParentId[OTHERS_ID] ?? []).length}
            isExpanded={expandedFacilityId === OTHERS_ID}
            onClick={() => setExpandedFacilityId(OTHERS_ID)}
            isSelected={selectedRoomParentId === OTHERS_ID}
          />
        )}
      </Stack>

      <Box sx={(theme) => ({ borderLeft: theme.mixins.borderValue() })} />

      <Stack sx={{ overflowY: 'auto', px: 1, gap: 0.5, py: 2, width: '50%' }}>
        {renderRoomsList()}
      </Stack>
    </Stack>
  );
};

type ExpandableRowProps = {
  name: string;
  count: number;
  isExpanded: boolean;
  onClick: () => void;
  isSelected: boolean;
};

const ExpandableRow: FC<ExpandableRowProps> = ({
  name,
  count,
  isExpanded,
  onClick,
  isSelected,
}) => {
  return (
    <DropdownSelectRow
      className={isExpanded ? EXPANDED_ROW_CLASS : undefined}
      isSelected={isSelected || isExpanded}
      endIcon={
        <Stack flexDirection="row" alignItems="center">
          <Icon sx={{ visibility: isSelected ? 'visible' : 'hidden', color: 'primary.main' }}>
            <DoneIcon />
          </Icon>
          <Counter
            inverse
            className="counter"
            sx={{
              minWidth: 20,
              height: 20,
              textAlign: 'center',
              background: 'transparent',
              borderColor: isSelected ? 'primary.main' : 'common.grey2',
              color: isSelected ? 'primary.main' : 'common.grey2',
            }}
          >
            {count}
          </Counter>
        </Stack>
      }
      onClick={onClick}
      sx={(theme) => ({
        px: 1,
        py: 0.5,
        '.MuiTypography-root': {
          whiteSpace: 'normal',
          overflowWrap: 'break-word',
          ...theme.typography.body1,
        },
        ...(isExpanded && { backgroundColor: 'common.lightBg' }),
        '&:hover': {
          '& .counter': {
            borderColor: 'primary.main',
            color: 'primary.main',
          },
        },
      })}
    >
      {name}
    </DropdownSelectRow>
  );
};
